import {
  FilterDocumentDateCustomTypes,
  FilterDocumentDateFilter,
  FilterDocumentDateFilterTypes,
} from '@griply/types';
import { format, isSameYear } from 'date-fns';
import { TFunction } from 'i18next';
import { DateFormatOptions } from 'shared/types/date-format-options';

const formatDateWithConditionalYear = (
  date: Date,
  dateFormat: DateFormatOptions,
  today: Date,
): string => {
  const isCurrentYear = isSameYear(date, today);
  const baseFormat =
    dateFormat === DateFormatOptions.DayMonthYear ? 'dd-MM' : 'MM-dd';
  const formatWithYear =
    dateFormat === DateFormatOptions.DayMonthYear ? "dd-MM-''yy" : "MM-dd-''yy";

  return format(date, isCurrentYear ? baseFormat : formatWithYear);
};

export const formatDeadlineFilterDisplay = (
  deadlineFilter: FilterDocumentDateFilter,
  t: TFunction,
  dateFormat: DateFormatOptions,
  today: Date,
): string => {
  if (deadlineFilter.type === FilterDocumentDateFilterTypes.Custom) {
    if (!deadlineFilter.custom) {
      return '';
    }

    const { start, end, type } = deadlineFilter.custom;

    if (type === FilterDocumentDateCustomTypes.Between && start && end) {
      const startFormatted = formatDateWithConditionalYear(
        start,
        dateFormat,
        today,
      );
      const endFormatted = formatDateWithConditionalYear(
        end,
        dateFormat,
        today,
      );
      return `${startFormatted} - ${endFormatted}`;
    }
    if (type === FilterDocumentDateCustomTypes.Before && end) {
      const endFormatted = formatDateWithConditionalYear(
        end,
        dateFormat,
        today,
      );
      return `≤ ${endFormatted}`;
    }
    if (type === FilterDocumentDateCustomTypes.After && start) {
      const startFormatted = formatDateWithConditionalYear(
        start,
        dateFormat,
        today,
      );
      return `≥ ${startFormatted}`;
    }

    return '';
  }

  const presetLabelMap: Record<
    Exclude<
      FilterDocumentDateFilterTypes,
      FilterDocumentDateFilterTypes.Custom
    >,
    string
  > = {
    [FilterDocumentDateFilterTypes.Overdue]:
      'context-menus.filtering.task.filters.date.presets.overdue',
    [FilterDocumentDateFilterTypes.Today]:
      'context-menus.filtering.task.filters.date.presets.today',
    [FilterDocumentDateFilterTypes.ThisWeek]:
      'context-menus.filtering.task.filters.date.presets.this-week',
    [FilterDocumentDateFilterTypes.NextWeek]:
      'context-menus.filtering.task.filters.date.presets.next-week',
    [FilterDocumentDateFilterTypes.Next14Days]:
      'context-menus.filtering.task.filters.date.presets.next-14-days',
    [FilterDocumentDateFilterTypes.ThisMonth]:
      'context-menus.filtering.task.filters.date.presets.this-month',
    [FilterDocumentDateFilterTypes.NextMonth]:
      'context-menus.filtering.task.filters.date.presets.next-month',
    [FilterDocumentDateFilterTypes.ThisQuarter]:
      'context-menus.filtering.task.filters.date.presets.this-quarter',
    [FilterDocumentDateFilterTypes.NextQuarter]:
      'context-menus.filtering.task.filters.date.presets.next-quarter',
    [FilterDocumentDateFilterTypes.ThisYear]:
      'context-menus.filtering.task.filters.date.presets.this-year',
    [FilterDocumentDateFilterTypes.NextYear]:
      'context-menus.filtering.task.filters.date.presets.next-year',
    [FilterDocumentDateFilterTypes.HasDeadline]:
      'context-menus.filtering.task.filters.date.presets.has-deadline',
    [FilterDocumentDateFilterTypes.NoDeadline]:
      'context-menus.filtering.task.filters.date.presets.no-deadline',
  };

  return t(presetLabelMap[deadlineFilter.type]);
};
